import React, { useState, useEffect, useCallback, memo } from 'react';
import api from '../services/api';
import './Secretarias.css';

const Secretarias = () => {
  const [secretarias, setSecretarias] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    id_cidade: '',
    sigla: '',
    descricao: '',
    status: 'ativo'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [secretariasRes, cidadesRes] = await Promise.all([
        api.get('/secretarias'),
        api.get('/cidades')
      ]);

      if (secretariasRes.data.success) {
        setSecretarias(secretariasRes.data.data);
      }
      if (cidadesRes.data.success) {
        setCidades(cidadesRes.data.data);
        if (cidadesRes.data.data.length > 0 && !formData.id_cidade) {
          setFormData(prev => ({ ...prev, id_cidade: cidadesRes.data.data[0].id_cidade }));
        }
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editing) {
        await api.put(`/secretarias/${editing.id_secretaria}`, formData);
        setSuccess('Secretaria atualizada com sucesso!');
      } else {
        await api.post('/secretarias', formData);
        setSuccess('Secretaria criada com sucesso!');
      }

      setShowModal(false);
      setEditing(null);
      setFormData({
        id_cidade: cidades.length > 0 ? cidades[0].id_cidade : '',
        sigla: '',
        descricao: '',
        status: 'ativo'
      });
      carregarDados();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar secretaria');
    }
  };

  const handleEdit = (secretaria) => {
    setEditing(secretaria);
    setFormData({
      id_cidade: secretaria.id_cidade,
      sigla: secretaria.sigla,
      descricao: secretaria.descricao,
      status: secretaria.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta secretaria?')) {
      return;
    }

    try {
      await api.delete(`/secretarias/${id}`);
      setSuccess('Secretaria excluída com sucesso!');
      carregarDados();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao excluir secretaria');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({
      id_cidade: cidades.length > 0 ? cidades[0].id_cidade : '',
      sigla: '',
      descricao: '',
      status: 'ativo'
    });
  };

  if (loading) {
    return (
      <div className="secretarias-container">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="secretarias-container">
      <div className="secretarias-header">
        <h1>Secretarias</h1>
        <button 
          className="btn-primary"
          onClick={() => setShowModal(true)}
        >
          + Nova Secretaria
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="secretarias-table-container">
        <table className="secretarias-table">
          <thead>
            <tr>
              <th>Cidade</th>
              <th>Sigla</th>
              <th>Descrição</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {secretarias.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  Nenhuma secretaria cadastrada
                </td>
              </tr>
            ) : (
              secretarias.map((secretaria) => (
                <tr key={secretaria.id_secretaria}>
                  <td>{secretaria.cidade?.sigla || '-'}</td>
                  <td><strong>{secretaria.sigla}</strong></td>
                  <td>{secretaria.descricao}</td>
                  <td>
                    <span className={`status-badge status-${secretaria.status}`}>
                      {secretaria.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(secretaria)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(secretaria.id_secretaria)}
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Editar Secretaria' : 'Nova Secretaria'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="id_cidade">Cidade *</label>
                <select
                  id="id_cidade"
                  value={formData.id_cidade}
                  onChange={(e) => setFormData({ ...formData, id_cidade: e.target.value })}
                  required
                  className="form-input"
                >
                  <option value="">Selecione uma cidade</option>
                  {cidades.map((cidade) => (
                    <option key={cidade.id_cidade} value={cidade.id_cidade}>
                      {cidade.sigla} - {cidade.descricao}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="sigla">Sigla *</label>
                <input
                  type="text"
                  id="sigla"
                  value={formData.sigla}
                  onChange={(e) => setFormData({ ...formData, sigla: e.target.value.toUpperCase() })}
                  required
                  maxLength={20}
                  className="form-input"
                  placeholder="Ex: EDU"
                />
              </div>

              <div className="form-group">
                <label htmlFor="descricao">Descrição *</label>
                <input
                  type="text"
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  required
                  maxLength={150}
                  className="form-input"
                  placeholder="Ex: Educação"
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  required
                  className="form-input"
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editing ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Secretarias;

